// Import pageBlocks as they are migrated
import {withBlockSpacing} from './blockSpacing'
import availableWheelDesigns from './availableWheelDesigns'
import blueBar from './blueBar'
import buttonList from './buttonList'
import callToAction from './callToAction'
import commonBlock from './commonBlock'
import compareTable from './compareTable'
import ctaGrid from './ctaGrid'
import customAddToCart from './customAddToCart'
import faqBlock from './faqBlock'
import featuredContent from './featuredContent'
import featureList from './featureList'
import heroCarousel from './heroCarousel'
import mapEmbed from './mapEmbed'
import multiColumnFeatures from './multiColumnFeatures'
import registrationCta from './registrationCta'
import richText from './richText'
import stepGuide from './stepGuide'
import stickyNav from './stickyNav'
import tableBlock from './tableBlock'
import team from './team'
import textWithImage from './textWithImage'
import transparentBoxes from './transparentBoxes'
import vehicleAlbum from './vehicleAlbum'
import vehicleGallery from './vehicleGallery'
import vehicleSelector from './vehicleSelector'
import videoCarousel from './videoCarousel'
import videoGrid from './videoGrid'
import wallpaper from './wallpaper'
import warranty from './warranty'
import wheelFeatures from './wheelFeatures'
import wheelOverview from './wheelOverview'
import wuFooForm from './wuFooForm'

// All page blocks have been migrated and imported above

// Note: Objects used as pageBlocks are already registered in the main schema registry
// No need to import/export them here - just reference them by type name

// used on dynamic page builder
// (dynamic - we add any kind of block to a page in sanity)
export const PageBlockTypes = [
  // Available pageBlocks
  { type: 'availableWheelDesigns' },
  { type: 'buttonList' },
  { type: 'callToAction' },
  { type: 'commonBlock' },
  { type: 'compareTable' },
  { type: 'ctaGrid' },
  { type: 'customAddToCart' },
  { type: 'faqBlock' },
  { type: 'featuredContent' },
  { type: 'featureList' },
  { type: 'fitmentBlock' },
  { type: 'hero' },
  { type: 'heroCarousel' },
  { type: 'mapEmbed' },
  { type: 'registrationCta' },
  { type: 'relatedArticles' },
  { type: 'richText' },
  { type: 'sizesPrices' },
  { type: 'stepGuide' },
  { type: 'stickyNav' },
  { type: 'tableBlock' },
  { type: 'team' },
  { type: 'textWithImage' },
  { type: 'transparentBoxes' },
  { type: 'vehicleAlbum' },
  { type: 'vehicleGallery' },
  { type: 'vehicleSelector' },
  { type: 'videoCarousel' },
  { type: 'videoGrid' },
  { type: 'wallpaper' },
  { type: 'warranty' },
  { type: 'wheelFeatures' },
  { type: 'wheelOverview' },
  { type: 'wuFooForm' },
]

// used on common page builder
// (common - we have pages with prefixed blocks of the kind)
// this one should not include `{ type: 'commonBlock' }`
export const commonBlockTypes = [
  // Available pageBlocks for common page builder
  { type: 'availableWheelDesigns' },
  { type: 'buttonList' },
  { type: 'callToAction' },
  { type: 'compareTable' },
  { type: 'faqBlock' },
  { type: 'featuredContent' },
  { type: 'featureList' },
  { type: 'fitmentBlock' },
  { type: 'hero' },
  { type: 'heroCarousel' },
  { type: 'mapEmbed' },
  { type: 'relatedArticles' },
  { type: 'richText' },
  { type: 'sizesPrices' },
  { type: 'stepGuide' },
  { type: 'tableBlock' },
  { type: 'team' },
  { type: 'textWithImage' },
  { type: 'transparentBoxes' },
  { type: 'vehicleAlbum' },
  { type: 'vehicleGallery' },
  { type: 'videoCarousel' },
  { type: 'videoGrid' },
  { type: 'wallpaper' },
  { type: 'warranty' },
  { type: 'wheelFeatures' },
  { type: 'wheelOverview' },
  { type: 'wuFooForm' },
]

//used on special event pages
export const SpecialEventPageBlocks = [
  {
    type: 'specialEventCountdown',
  },
  { type: 'specialEventSubscribe' },
  { type: 'specialEventRegistration' },
  { type: 'specialEventPromotions' },
  {
    type: 'socialMedia',
  },
  {
    type: 'galleryBox',
  },
  {
    type: 'specialEventHero',
  },
]

// Blocks available inside a legacy special event phase (blackFriday, memorialDay,
// taxRefund, comingSoon). Promo landers also embed standard blocks — the
// `vehicleSelector` (Shop by vehicle bar), `richText` and the compact
// `registrationCta` (APE-38). They are not special-event-only, so keeping them out
// of `SpecialEventPageBlocks` leaves them available in the standard content section.
export const SpecialEventPhaseBlocks = [
  ...SpecialEventPageBlocks,
  { type: 'vehicleSelector' },
  { type: 'richText' },
  { type: 'registrationCta' },
]

// Unified block types - combines all page blocks + special event blocks
// Used by the unified dynamic pages system
export const UnifiedPageBlockTypes = [...PageBlockTypes, ...SpecialEventPageBlocks]

// Block types that only make sense on phased (date-driven) pages — e.g. the countdown
// derives its target from the phase's dates. Excluded from the standard content section.
const SPECIAL_EVENT_TYPES = SpecialEventPageBlocks.map((b) => b.type)

// Insert menu config with groups - the `of` property assigns types to groups.
// includeSpecialEvents=false (standard content section) strips special-event blocks
// from every group and drops any group left empty.
export const getBlockInsertMenuConfig = ({ includeSpecialEvents = true } = {}) => {
  const groups = [
    {
      name: 'heroes',
      title: 'Heroes & Banners',
      of: ['hero', 'heroCarousel', 'specialEventHero', 'wallpaper'],
    },
    {
      name: 'content',
      title: 'Content',
      of: ['richText', 'textWithImage', 'featuredContent', 'featureList'],
    },
    {
      name: 'media',
      title: 'Media & Gallery',
      of: ['vehicleGallery', 'vehicleAlbum', 'videoCarousel', 'videoGrid', 'galleryBox'],
    },
    {
      name: 'wheels',
      title: 'Wheels & Products',
      of: ['wheelOverview', 'wheelFeatures', 'availableWheelDesigns', 'sizesPrices', 'customAddToCart'],
    },
    {
      name: 'vehicles',
      title: 'Vehicles',
      of: ['vehicleSelector', 'fitmentBlock'],
    },
    {
      name: 'navigation',
      title: 'Navigation & CTAs',
      of: ['callToAction', 'registrationCta', 'ctaGrid', 'buttonList', 'stickyNav', 'transparentBoxes'],
    },
    {
      name: 'tables',
      title: 'Tables & Data',
      of: ['tableBlock', 'compareTable', 'faqBlock', 'stepGuide'],
    },
    {
      name: 'special',
      title: 'Special Events',
      of: [
        'specialEventCountdown',
        'specialEventSubscribe',
        'specialEventRegistration',
        'specialEventPromotions',
        'socialMedia',
      ],
    },
    {
      name: 'other',
      title: 'Other',
      of: ['commonBlock', 'relatedArticles', 'team', 'warranty', 'mapEmbed', 'wuFooForm'],
    },
  ]

  if (includeSpecialEvents) return { groups }

  // Standard content section: strip special-event blocks from every group, drop empties.
  return {
    groups: groups
      .map((g) => ({ ...g, of: g.of.filter((t) => !SPECIAL_EVENT_TYPES.includes(t)) }))
      .filter((g) => g.of.length > 0),
  }
}

const PageBlocks = [
  // Only include pageBlocks that are not already registered as objects
  availableWheelDesigns,
  // APE-34: blueBar and multiColumnFeatures have no frontend renderer, so they are kept
  // out of the block lists and insert menu above. Add them back once a renderer exists.
  blueBar,
  buttonList,
  callToAction,
  commonBlock,
  compareTable,
  ctaGrid,
  customAddToCart,
  faqBlock,
  featuredContent,
  featureList,
  heroCarousel,
  mapEmbed,
  multiColumnFeatures,
  registrationCta,
  richText,
  stepGuide,
  stickyNav,
  tableBlock,
  team,
  textWithImage,
  transparentBoxes,
  vehicleAlbum,
  vehicleGallery,
  vehicleSelector,
  videoCarousel,
  videoGrid,
  wallpaper,
  warranty,
  wheelFeatures,
  wheelOverview,
  wuFooForm,
]

// AR-1800: every page block gets the shared "Spacing" control.
export default PageBlocks.map(withBlockSpacing)
